import { useEffect, useRef } from "react";

function clampIndex(index, length) {
  if (!length) return 0;
  if (!Number.isFinite(Number(index))) return 0;
  return Math.min(Math.max(Number(index), 0), length - 1);
}

/**
 * Domain-neutral inline hero + thumbnail image gallery.
 * Adapters supply items, active index, and hero-click intent.
 */
function InlineImageGallery({
  items = [],
  activeIndex = 0,
  onActiveIndexChange,
  onHeroClick,
  heroTriggerRef = null,
  emptyLabel = "Фото не додано",
  ariaLabel = "Галерея",
  className = "",
  testIds = {},
}) {
  const thumbsRef = useRef(null);
  const length = Array.isArray(items) ? items.length : 0;
  const safeActive = clampIndex(activeIndex, length);
  const hero = length ? items[safeActive] : null;
  const heroTestId = testIds.hero || "inline-image-gallery-hero";
  const prevTestId = testIds.prev || "inline-image-gallery-prev";
  const nextTestId = testIds.next || "inline-image-gallery-next";
  const thumbTestId = testIds.thumb || ((index) => `inline-image-gallery-thumb-${index}`);

  const select = (index) => {
    if (!length) return;
    onActiveIndexChange?.(clampIndex(index, length));
  };

  useEffect(() => {
    if (!length) return undefined;
    const root = thumbsRef.current;
    if (!root) return undefined;
    const active = root.querySelector('[aria-current="true"]');
    if (active && typeof active.scrollIntoView === "function") {
      active.scrollIntoView({
        behavior: "smooth",
        inline: "center",
        block: "nearest",
      });
    }
    return undefined;
  }, [safeActive, length]);

  if (!length) {
    return (
      <div
        className={["inline-image-gallery", className].filter(Boolean).join(" ")}
        aria-label={ariaLabel}
        role="group"
      >
        <div className="inline-image-gallery__empty">{emptyLabel}</div>
      </div>
    );
  }

  const showNav = length > 1;
  const heroKind = hero?.kind || "image";
  const heroBadge = hero?.badge;
  const heroInteractive = typeof onHeroClick === "function";
  const heroClassName = [
    "inline-image-gallery__hero",
    heroKind === "plan" ? "inline-image-gallery__hero--plan" : "",
  ]
    .filter(Boolean)
    .join(" ");
  const heroInner = (
    <>
      <img src={hero.src} alt={hero.alt || ""} />
      {heroBadge ? (
        <span
          className={[
            "inline-image-gallery__badge",
            heroKind === "image" ? "inline-image-gallery__badge--photo" : "",
          ]
            .filter(Boolean)
            .join(" ")}
        >
          {heroBadge}
        </span>
      ) : null}
    </>
  );

  return (
    <div
      className={["inline-image-gallery", className].filter(Boolean).join(" ")}
      aria-label={ariaLabel}
      role="group"
    >
      <div className="inline-image-gallery__hero-wrap">
        {showNav ? (
          <button
            type="button"
            className="inline-image-gallery__nav inline-image-gallery__nav--prev"
            aria-label="Попереднє фото"
            data-testid={prevTestId}
            onClick={() => select((safeActive - 1 + length) % length)}
          >
            <i className="fa-solid fa-chevron-left" aria-hidden="true" />
          </button>
        ) : null}
        {heroInteractive ? (
          <button
            ref={heroTriggerRef}
            type="button"
            className={heroClassName}
            onClick={() => onHeroClick(safeActive)}
            aria-label={hero.heroLabel || undefined}
            data-testid={heroTestId}
            data-kind={heroKind}
            data-active-index={safeActive}
          >
            {heroInner}
          </button>
        ) : (
          <div
            className={heroClassName}
            data-testid={heroTestId}
            data-kind={heroKind}
            data-active-index={safeActive}
          >
            {heroInner}
          </div>
        )}
        {showNav ? (
          <button
            type="button"
            className="inline-image-gallery__nav inline-image-gallery__nav--next"
            aria-label="Наступне фото"
            data-testid={nextTestId}
            onClick={() => select((safeActive + 1) % length)}
          >
            <i className="fa-solid fa-chevron-right" aria-hidden="true" />
          </button>
        ) : null}
      </div>
      {showNav ? (
        <ul
          ref={thumbsRef}
          className="inline-image-gallery__thumbs"
          aria-label="Мініатюри"
        >
          {items.map((item, index) => {
            const selected = index === safeActive;
            return (
              <li
                key={item.id || `${item.src}-${index}`}
                className={[
                  item.kind === "plan" ? "inline-image-gallery__item--plan" : "",
                  selected ? "is-selected" : "",
                ]
                  .filter(Boolean)
                  .join(" ")}
              >
                <button
                  type="button"
                  className={selected ? "is-active" : ""}
                  onClick={() => select(index)}
                  aria-current={selected ? "true" : undefined}
                  aria-label={item.alt || `Фото ${index + 1}`}
                  data-testid={
                    typeof thumbTestId === "function"
                      ? thumbTestId(index)
                      : `${thumbTestId}-${index}`
                  }
                >
                  <img src={item.src} alt="" />
                  {item.kind === "plan" && item.badge ? (
                    <span className="inline-image-gallery__badge">{item.badge}</span>
                  ) : null}
                </button>
              </li>
            );
          })}
        </ul>
      ) : null}
    </div>
  );
}

export default InlineImageGallery;
export { clampIndex };
